import React from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';

import { QuizContext } from '../../../../context/QuizContext';
import Explanation, { LoadingExplanation } from '../learn/Explanation';

const SingleChoice = ({ user, quiz, question, explain, saveQuiz, courseId, subsectionId }) => {
    const { answer, setAnswer } = React.useContext(QuizContext);
    const { explanations, setExplanations } = React.useContext(QuizContext);
    const { loading, setLoading } = React.useContext(QuizContext);
    const { fetched, setFetched } = React.useContext(QuizContext);

    React.useEffect(() => {
        if (!answer) setAnswer(null);
        if (!explanations) setExplanations(Array(question.answers.length).fill({ explanation: null, show: false }));
    });

    React.useEffect(() => {
        setExplanations(Array(question.answers.length).fill({ explanation: null, show: false }));
        fetch();
    }, [user.courses]);

    React.useEffect(() => {
        if (fetched) return;
        if (answer) return;

        fetch();
    }, [answer]);

    const fetch = async () => {
        //  Default values
        setAnswer(question.answers.find((a) => a.id === user.courses.find((c) => c.id === courseId)
            ?.quiz?.find((q) => q.id === subsectionId)
            ?.questions?.find((q) => q.id === question.id)
            ?.answers[0]
            ?.id)
            ?.text
        );
        setFetched(true);

        //  Generate explanation for incorrect answer
        const userAnswer = user.courses.find((c) => c.id === courseId)
            ?.quiz?.find((q) => q.id === subsectionId)
            ?.questions?.find((q) => q.id === question.id)
            ?.answers[0]
            ?.id;
        if (userAnswer) {
            const userAnswerText = question.answers.find((a) => a.id === userAnswer).text;
            const index = question.answers.findIndex((a) => a.text === userAnswerText);
            const newExplanations = [...explanations];

            if (userAnswerText !== question.answers.filter((a) => a.is_correct)[0]?.text) {
                setLoading(true);

                if (user.courses.find((c) => c.id === courseId)
                    ?.quiz?.find((q) => q.id === subsectionId)
                    ?.questions?.find((q) => q.id === question.id)
                    ?.answers[0]
                    ?.explanation) {
                    newExplanations[index] = {
                        explanation: user.courses.find((c) => c.id === courseId)
                            ?.quiz?.find((q) => q.id === subsectionId)
                            ?.questions?.find((q) => q.id === question.id)
                            ?.answers[0]
                            ?.explanation, show: true
                    };
                    setExplanations(newExplanations);
                    setLoading(false);
                } else if (explanations[index]?.explanation) {
                    newExplanations[index] = { explanation: explanations[index].explanation, show: true };
                    setExplanations(newExplanations);
                } else {
                    if (quiz.type !== 'exam') {
                        const explanation = await explain(question, userAnswerText);
                        if (explanation) {
                            newExplanations[index] = { explanation: explanation, show: true };

                            setLoading(false);
                            setExplanations(newExplanations);
                        }
                    }
                }
            } else {
                newExplanations[index] = { explanation: explanations[index]?.explanation, show: false };
                setExplanations(newExplanations);
            }
        }
    }

    const handleChange = async (event) => {
        setAnswer(event.target.value);

        //  Explanation
        const newExplanations = [...explanations];
        const index = question.answers.findIndex((a) => a.text === event.target.value);

        if (quiz.type !== 'exam') {
            if (event.target.value !== question.answers.filter((a) => a.is_correct)[0].text) {
                setLoading(true);

                if (explanations[index]?.explanation) {
                    newExplanations[index] = { explanation: explanations[index].explanation, show: true };
                    setExplanations(newExplanations);
                    setLoading(false);
                } else {
                    const explanation = await explain(question, event.target.value);
                    if (explanation) {
                        newExplanations[index] = { explanation: explanation, show: true };

                        setLoading(false);
                        setExplanations(newExplanations);
                    }
                }
            } else {
                for (let i = 0; i < question.answers.length; i++) {
                    newExplanations[i] = { explanation: explanations[i]?.explanation, show: false };
                }
                setExplanations(newExplanations);
            }
        }

        //  Store answer
        let id;
        for (let i = 0; i < question.answers.length; i++) {
            if (event.target.value === question.answers[i].text) {
                id = question.answers[i].id;
                break;
            }
        }
        saveQuiz(question, [id], newExplanations);
    }

    return (
        <FormControl>
            <RadioGroup
                value={answer}
                onChange={handleChange}
            >
                {question.answers.map((a, index) => (
                    <div key={`${a.id}_${index}`}>
                        <FormControlLabel
                            key={index}
                            value={a.text}
                            label={a.text}
                            control={
                                <Radio disabled={user.role !== 'student'} size='small' sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                        color: a.is_correct || quiz.type === 'exam' ? 'primary.main' : 'error.main'
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent'
                                    }
                                }} />
                            }
                            sx={{ '& .MuiTypography-root': { fontSize: '0.975rem' } }}
                        />
                        {(explanations.length > 0 && explanations[index]?.show && !loading && quiz.type !== 'exam') &&
                            <Explanation explanation={explanations[index]?.explanation} mb={index === question.answers.length - 1} />
                        }
                        {(!a.is_correct && a.text === answer && loading && quiz.type !== 'exam') &&
                            <LoadingExplanation />
                        }
                    </div>
                ))}
            </RadioGroup>
        </FormControl>
    );
}

export default SingleChoice;